/* eslint-disable */
import { BrowserModule, Meta, Title } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, inject, provideAppInitializer } from '@angular/core';

import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { AuthInterceptor } from './auth.interceptor';

import {
  WindowRef,
  ENVIRONMENT_CONFIG as COMPONENT_LIB_ENVIRONMENT_CONFIG,
  Environment as ComponentLibEnvironment,
  ExternalLinkModule,
  PageLayoutModule,
  SecondaryNavigationModule,
  WindowRefModule
} from '@vanguard/secure-site-components-lib';


import { AppRoutingModule } from './app-routing.module';
import { ButtonModule } from '@vg-constellation/angular-19/button';
import { SelectModule } from '@vg-constellation/angular-19/select';
import { IconModule } from '@vg-constellation/angular-19/icon';
import { LinkModule } from '@vg-constellation/angular-19/link';
import { CardModule } from '@vg-constellation/angular-19/card';
import { ModalDialogComponent, ModalDialogModule } from '@vg-constellation/angular-19/modal-dialog';
import { environment } from '../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { ConfirmService } from '../app/services/confirm/confirm.service';
import { CaasService } from './services/caas/caas.service';
import { RedirectService } from './services/redirect/redirect.service';
import { AppComponent } from './app.component';
import { ConfirmComponent } from '../app/components/confirm/confirm.component';
import { TetriaryNavigationComponent } from '../app/components/tetriary-navigation/tetriary-navigation.component';
import { LoginErrorComponent } from './components/login-error/login-error.component';
import { SpinnerModule } from '@vg-constellation/angular-19/spinner';
import { GatekeeperService } from './services/gatekeeper/gatekeeper.service';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from '@vg-constellation/angular-19/dropdown';
import { StickyHeaderDirective } from './directives/sticky-header.directive';
import { NavOverflowComponent, NavOverflowModule } from '@vg-constellation/angular-19/nav-overflow';
import { StoreModule } from '@ngrx/store';
import { SendconfirmationHomeModule } from 'src/app/components/send-confirmation/send-confirmation.module';
import { confirmationReducer } from './state/reducers/reducer';
import { InternalCalloutComponent } from '@vg-constellation/angular-19/internal-callout';
import { BannerModule } from '@vg-constellation/angular-19/banner';
import { StatusReviewComponent } from './components/status-review/status-review.component';

@NgModule({ declarations: [AppComponent, ConfirmComponent, TetriaryNavigationComponent, StickyHeaderDirective, LoginErrorComponent,StatusReviewComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [BrowserModule, AppRoutingModule, ButtonModule, SelectModule, SpinnerModule, IconModule, ModalDialogModule.forRoot(),
    LinkModule,
    CardModule,
    PageLayoutModule,
    SecondaryNavigationModule,
    WindowRefModule,
    ExternalLinkModule,
    FormsModule,
    DropdownModule, NavOverflowComponent,
    StoreModule.forRoot({ confirmationFlow: confirmationReducer }), InternalCalloutComponent, BannerModule], providers: [ConfirmService, ModalDialogComponent,
        SendconfirmationHomeModule,
        CookieService,
        CaasService,
        GatekeeperService,
        NavOverflowComponent,
        {
            provide: COMPONENT_LIB_ENVIRONMENT_CONFIG,
            useValue: {
                isProd: environment.production, // a boolean indicating whether library is being used in a production environment
                appName: 'confirmations.frontend', // a string identifying the app hosting the component library.
                isPerf: environment.performance,
                isInternal: environment.internal
            } as ComponentLibEnvironment,
        },
        provideAppInitializer(() => {
        const initializerFn = (function (http: HttpClient, meta: Meta, windowRef: WindowRef, cookieService: CookieService, caasService: CaasService, gatekeeperservice: GatekeeperService, redirectService: RedirectService) {
                const $window: Window = windowRef.nativeWindow;
                return async () => {
                    // If POID is missing on internal environments, skip AEM network calls to prevent false SLO drops.
                    const skipAemNetworkCall = environment.internal && !cookieService.get('POID');

                    // If the URL contains 'login-error', we do not proceed with the initialization.
                    const currentUrl = new URL(window.location.href);
                    console.log('=== APP INITIALIZER RUNNING ===');
                    console.log('Current URL in app initializer:', currentUrl.href);
                    console.log('Current pathname:', currentUrl.pathname);

                    if (currentUrl.href.includes('login-error')) {
                        console.log('Loading CaaS data for login-error page (skipping Adobe/Gatekeeper)');
                        await caasService.getContent(skipAemNetworkCall);
                        $window.document.body.hidden = false;
                        return Promise.resolve();
                    }

                    // Skip initialization for internal routes but still load CaaS content
                    if (currentUrl.pathname.includes('/send-confirmation') || currentUrl.pathname.includes('/status-review')) {
                        console.log('Skipping Adobe API call for internal route:', currentUrl.pathname);
                        await caasService.getContent(skipAemNetworkCall);
                        window.document.body.hidden = false;
                        return Promise.resolve();
                    }

                    if (skipAemNetworkCall) {
                        await caasService.getContent(skipAemNetworkCall);
                        redirectService.redirectTo('/login-error?reason=poid-missing');
                        return Promise.resolve();
                    }

                    await http
                        .get(`${environment.CONFIRMATIONS.BASE_URL}/adobe`, { withCredentials: true })
                        .toPromise()
                        .then(async (data: any) => {
                        /* Calling CAAS service */
                        await caasService.getContent(skipAemNetworkCall);
                        const spoid = data.sPOID;
                        meta.updateTag({ name: 'DCSext.poid', content: data.poid });
                        meta.updateTag({ name: 'DCSext.sPoid', content: spoid }); // Used for Floodlight
                        meta.updateTag({ name: 'WT.seg_6', content: data.crewFlag });
                        $window.spoid = spoid; // Used for Medallia
                        ($window as any).poid = data.poid; // Used for Medallia POID exclusion check
                        $window.document.body.hidden = false;
                        gatekeeperservice.getGatekeeperFlag();
                    })
                        .catch(async () => {
                        /*
                         * If Adobe call fails & POID is present in browser cookies (POID will not be present for external users) &
                         * 'secureMessage' flag is present in the url that means this is an internal user.
                         * Redirect to statements-i.webt.vanguard.com
                        */
                        const currentUrl = new URL(window.location.href);
                        const goToInternal = currentUrl.searchParams.get('secureMessage');

                        // Don't redirect if user is already on internal application routes
                        const isInternalRoute = currentUrl.pathname.includes('/send-confirmation') || currentUrl.pathname.includes('/status-review');

                        console.log('Adobe API call failed. Current path:', currentUrl.pathname);
                        console.log('Is internal route:', isInternalRoute);

                        // Load CaaS content before redirecting so the page has content
                        // if the redirect is slow or fails
                        await caasService.getContent(skipAemNetworkCall);
                        $window.document.body.hidden = false;

                        if (isInternalRoute) {
                            console.log('User is on internal route, skipping redirect');
                            window.document.body.hidden = false;
                            return Promise.resolve();
                        }

                        if (cookieService.get('POID') && goToInternal) {
                            console.log('Redirecting to internal URL');
                            redirectService.redirectTo(environment.CONFIRMATIONS.INTERNAL_URL);
                            return Promise.resolve();
                        }
                        else {
                            console.log('Redirecting to logon URL');
                            redirectService.redirectTo(environment.LOGON_URL);
                            // Ensure that app initialization is aborted since we're going to the (external) logon page.
                            // return Promise.reject();
                            return Promise.resolve();
                        }
                    });
                };
            })(inject(HttpClient), inject(Meta), inject(WindowRef), inject(CookieService), inject(CaasService), inject(GatekeeperService), inject(RedirectService));
        return initializerFn();
      }),
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        Title, provideHttpClient(withInterceptorsFromDi())] })
export class AppModule { }
